import React, { useState, useEffect, useContext } from 'react';
import { CircularProgress, Box, Typography, TableSortLabel, TableCell, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MUIDataTable from "mui-datatables";
import axios from 'axios';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import AuthContext from '../../contexts/AuthContext';
import { useDispatch } from 'react-redux';
import { setMetaData, setSweetAlert, setSnackDetails } from '../../service/actions/common';

const TransactionsList = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const formatOptions = JSON.parse(process.env.REACT_APP_DATE_TIME_FORMAT || '{}');
	const { token, user } = useContext(AuthContext);
	const [loading, setLoading] = useState(true);
	const [transactions, setTransactions] = useState([]);
	const [searchText, setSearchText] = useState('');
	const [totalRows, setTotalRows] = useState(0);
	const [perPage, setPerPage] = useState( parseInt(process.env.REACT_APP_PER_PAGE) );
	const [page, setPage] = useState(0);
	const [sortOrder, setSortOrder] = useState({ name: 'createdAt', direction: 'desc' }); 

	const metaData  = {
		title: 'Transactions | Inspect Reply AI',
		description: 'Transactions | Inspect Reply AI'
	};

	const catchError = async (error) => {
		if (error.response) {
		if (error.response.status === 401) {
			navigate('/sign-in');
		} else if (error.response.status >= 500) {
			dispatch(setSweetAlert({ open: true, title: 'Server Error', text: '' }));
		} else if (error.response.status === 400) {
			dispatch(setSnackDetails({openState : true, message : error.response.data.message, severity : 'error'}));
			return false;
		} else {
			dispatch(setSweetAlert({ open: true, title: 'Error', text: '' }));
		}
	} else if (error.request) {
		dispatch(setSweetAlert({ open: true, title: 'Connection Lost', text: '' }));
	} else {
		dispatch(setSweetAlert({ open: true, title: 'Error', text: 'An unknown error occurred.' }));
	}
	};

  	const getMuiTheme = () =>
    createTheme({
      components: {
		MuiTableSortLabel: {
			styleOverrides: {
				icon: {
				opacity: '1 !important', // Ensure sorting icons are always visible
				},
			},
		},
        MuiTableCell: {
          styleOverrides: {
            head: {
              '& .MuiButtonBase-root': {
                fontWeight: 'bold', // Make sorting label text bold as well
                color: 'inherit', // Keep color inheritance for both text and icon
              },
            },
          },
        },
        MuiTableRow: {
          styleOverrides: {
            root: {
              '&.MuiTableRow-root': {
                  cursor: 'pointer',
              },
              "&:nth-of-type(odd)": {
                backgroundColor: "#FFFFFF",
              },
              "&:nth-of-type(even)": {
                backgroundColor: "#D8D8D8",
              },
              "& .MuiTableCell-head": {
                  backgroundColor: "#D8D8D8",
                  fontWeight: "bold"
              },
            },
          },
        },
      },
	});

	const handlePageChange = (currentPage) => {
		setPage(currentPage);
	};

	const fetchTransactions = async (page, perPage, searchText, sortOrder) => {
		if (searchText.trim() === '') {
			setLoading(true);
		}
		if (user && token) {
		try {
			let uri = `/transactions?skip=${page * perPage}&limit=${perPage}&sort=${sortOrder.name}&sort_dir=${sortOrder.direction}`
			if (searchText.trim() !== '') {
				uri += `&search=${encodeURI(searchText.trim())}`
			}
			const response = await axios.get(uri, {
				headers: {
				'Authorization': `Bearer ${token}`,
				'Content-Type': 'application/json',
				},
			});
			setTransactions(response.data.transactions);
			setTotalRows(response.data.total);
			setLoading(false);
		} catch (error) {
			catchError(error);
		}
		}
	};

	useEffect(() => {
		fetchTransactions(page, perPage, searchText, sortOrder);
		dispatch(setMetaData(metaData));
		// eslint-disable-next-line
	}, [page, perPage, searchText, sortOrder]);

	const handleSearchChange = (value) => {
		if (value.length > 2 ) {
			setSearchText(value.trim());
			setPage(0); // Reset to first page on search
		} else if (value.length === 0) {
			setSearchText(" ");
		}
	};

 	// Common customHeadRender function
	const customHeadRender = (columnMeta) => (
		<TableCell key={columnMeta.name}>
		<Box display="flex" alignItems="center">
			<Typography variant="body1" sx={{ fontWeight: 'bold' }}>{columnMeta.label}</Typography>
			{columnMeta.sort && (
				<TableSortLabel
					active={sortOrder.name === columnMeta.name}
					direction={sortOrder.name === columnMeta.name ? sortOrder.direction : 'desc'}
					onClick={() => handleSortChange(columnMeta.name)}
				/>
			) }
		</Box>
		</TableCell>
	);

  	// Handle sort change
	const handleSortChange = (columnName) => {
		const isAsc = sortOrder.name === columnName && sortOrder.direction === 'asc';
		setSortOrder({ name: columnName, direction: isAsc ? 'desc' : 'asc' });
		setPage(0);
	};

  const columns = [
	{
		name: "_id",
		options: {
			display: false
		}
	},
    {
		name: "txn_id",
		label: "Transaction ID",
		options: {
			filter: true,
			sort: true,
			customHeadRender: (columnMeta) => customHeadRender(columnMeta),
     	}
    },
    {
		name: "customer",
		label: "User",
		options: {
			filter: false,
			sort: true,
			customHeadRender: (columnMeta) => customHeadRender(columnMeta),
			customBodyRender: (value, tableMeta, updateValue) => {
				if (value && value.email) {
					return (
						// eslint-disable-next-line
						<Link href={ 'mailto:' + `${value.email}`} target="_blank">
						  {value.email}
						</Link>
					  ); 
				} else 
				{ 
					return ''; 
				}
			}
		}
	},
	{
		name: "payment_platform",
		label: "Platform",
		options: {
			filter: true,
			sort: false,
			customHeadRender: (columnMeta) => customHeadRender(columnMeta)
      	}
	},
	{
		name: "amount",
		label: "Amount",
		options: {
			filter: false,
			sort: true,
			customHeadRender: (columnMeta) => customHeadRender(columnMeta),
			customBodyRender: (value, tableMeta, updateValue) => {
				if (value) { 
					return '$' + value; 
				} else 
				{ 
					return ''; 
				}
			}
		}
	},
	{
		name: "createdAt",
		label: "Date & Time",
		options: {
			filter: true,
			sort: true,
			customHeadRender: (columnMeta) => customHeadRender(columnMeta),
			customBodyRender: (value, tableMeta, updateValue) => {
				const today = Date.parse(value);
				return new Intl.DateTimeFormat('en-US', formatOptions).format(today);
			}
		}
    },
  ];

  const options = {
    serverSide: true,
    viewColumns: false,
    count: totalRows,
    rowsPerPage: perPage,
    rowsPerPageOptions: process.env.REACT_APP_PER_PAGE_OPTION.split(",").map(Number),
    page: page,
    searchText: searchText,
    loading: loading,
    selectableRows: 'none',
    print: false,
    filter: false,
    download: true,
    sortOrder: {
		name: sortOrder.name,
		direction: sortOrder.direction,
	},
    onTableChange: (action, tableState) => {
      console.log(action, tableState);
      if (action === "changePage") {
        handlePageChange(tableState.page);
      } else if (action === "search") {
        handleSearchChange(tableState.searchText || "");
      } else if (action === 'changeRowsPerPage') {
        setPerPage(tableState.rowsPerPage);
        setPage(0);
      } else if (action === 'onSearchClose') {
        handleSearchChange(""); // reset searchText to null
      }
    },
    onDownload: (buildHead, buildBody, columns, data) => {
      const alteredData = data?.map((row, index) => ({
        index,
        data: row?.data?.map((field, index) => {
                  if (columns[index].customBodyRender) {
                      return columns[index].customBodyRender(field)
                  }
                  return field
              }),
      }))
      return `${buildHead(columns)}${buildBody(alteredData)}`.trim()
    },
    textLabels: {
      toolbar: {
        search: "Search",
        downloadCsv: "Download CSV",
      },
      body: {
        noMatch: 'No results matching your filter. Try removing the Filter',
      }
    },
  };

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <Typography variant="h4" gutterBottom>Transactions</Typography>
      
      <Box sx={{ backgroundColor:'#FFF', p:2, height: '100vh' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ThemeProvider theme={getMuiTheme()}>
            <MUIDataTable
              data={transactions}
              columns={columns}
              options={options}
            />
          </ThemeProvider>
        )}
      </Box>
    </Box>
  );
};

export default TransactionsList;
